import { useState } from 'react';
import { type CatalogScheduledQueue } from '@/services/catalogApi.service';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface ScheduledQueuePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduledQueues: CatalogScheduledQueue[];
  highlightQueueId?: string;
  availableQueues: Array<{
    id: string;
    nome: string;
    realStatus: string;
  }>;
  onAdd: (queueId: string, scheduledTime: string) => void;
}

export default function ScheduledQueuePicker({
  open,
  onOpenChange,
  scheduledQueues,
  highlightQueueId,
  availableQueues,
  onAdd,
}: ScheduledQueuePickerProps) {
  const [selectedQueueId, setSelectedQueueId] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [timeError, setTimeError] = useState('');

  const queuesForPicker = availableQueues.filter((q) => {
    const isHighlight = q.id === highlightQueueId;
    const isAlreadyScheduled = scheduledQueues.some((sq) => sq.queueId === q.id);
    return !isHighlight && !isAlreadyScheduled;
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedQueueId('');
      setSelectedTime('');
      setTimeError('');
    }
    onOpenChange(open);
  };

  const handleTimeChange = (value: string) => {
    setSelectedTime(value);
    if (timeError) {
      setTimeError('');
    }
  };

  const handleSubmit = () => {
    if (!selectedQueueId || !selectedTime) return;

    const isDuplicateTime = scheduledQueues.some(
      (sq) => sq.scheduledTime === selectedTime
    );

    if (isDuplicateTime) {
      setTimeError('Já existe uma fila agendada para este horário');
      return;
    }

    onAdd(selectedQueueId, selectedTime);
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adicionar Fila Agendada</DialogTitle>
          <DialogDescription>
            Selecione uma fila e defina o horário de exibição no catálogo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="queue-select">Fila</Label>
            {queuesForPicker.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma fila disponível para agendar.
              </p>
            ) : (
              <Select value={selectedQueueId} onValueChange={setSelectedQueueId}>
                <SelectTrigger id="queue-select">
                  <SelectValue placeholder="Selecione uma fila" />
                </SelectTrigger>
                <SelectContent>
                  {queuesForPicker.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="time-input">Horário</Label>
            <input
              id="time-input"
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            {timeError && (
              <p className="text-sm text-destructive" data-testid="time-error">
                {timeError}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedQueueId || !selectedTime}
          >
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
